import React, { Suspense, useEffect, useMemo, useState } from 'react';

import Badge from '@mui/material/Badge';
// Correct makeStyles import for MUI v5 if needed:
// import { makeStyles } from '@mui/styles'; // or use sx prop
import makeStyles from '@mui/styles/makeStyles'; // Keep if using MUI v4 or appropriate setup
import Typography from '@mui/material/Typography';
import { Notifications } from '@mui/icons-material';
import Container from 'components/Container';
import Spinner from 'components/Spinner';
import { calculateMonthlyRentPerAdjustedGla, getAdjustedGlaByAsset } from 'helpers/adjustedGla';
import { formatNumberToCurrency, formatZillions, numberWithCommas } from 'helpers/utils';
import UserHasAccess from 'hooks/UserHasAccess';
import useUrlParams from 'hooks/useUrlParams';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from 'store/actions';
import AGGridTable from '../AGGrid';
import { toast } from 'react-toastify';
import Icon from '@mui/material/Icon';
import Button from '@mui/material/Button';
import { openModal } from '../../components/Modal/modalReducer';
import clsx from 'clsx';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Grow from '@mui/material/Grow';
import useStyles from 'styles/tables';
import useRouter from '../../hooks/useRouter';
import ApexChart from '../../components/Charts';
import Tabs from '../../components/Tabs/Tabs';
import TabPanel from '../../components/Tabs/TabPanel';
import Paper from '@mui/material/Paper';
import AGGridHeader from '../AGGrid/AGGridHeader';
import CoreFilters from '../CoreFilters';
import GCPI from './AssetsDetails/GCPI';
import { getUserTableViews } from 'store/actions';
// TableContainer import removed as it wasn't used in the return

// If using MUI v5, prefer sx prop or styled-components over makeStyles
const useLocalStyles = makeStyles((theme) => ({
  icon: {
    color: '#CCCCCC'
  },
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6)
  },
  badge: {
    minWidth: theme.spacing(2.2),
    height: theme.spacing(2.2),
    width: theme.spacing(2.2)
  },
  chartContainer: {
    // Base styles for the container Box
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  chartTitle: {
    // Class for consistent title styling
    textAlign: 'center',
    marginBottom: theme.spacing(1), // Reduced margin slightly due to padding in Box
    fontWeight: 500,
    color: '#333333'
  }
}));

const Assets = () => {
  const classes = useStyles();
  const localClasses = useLocalStyles();
  const dispatch = useDispatch();
  const { page, portfolioIds, assetIds, assetType, assetsStatus, managers } = useUrlParams();
  const { loaded, data, error } = useSelector((state) => state.assets.assets);
  const { loaded: reportsLoaded, data: asset_reports } = useSelector(
    (state) => state.reports.asset_reports
  );
  const userPermissions = useSelector((state) => state.user.userPermissions.permissionsList);
  const EditAssetsPermissions = UserHasAccess('asset', 'edit', userPermissions);
  const router = useRouter();
  const [api, setAPI] = useState();
  const [tab, setTab] = useState(() => 0);
  const [filter, setFilter] = useState({});
  const [tableSettingsChanged, setTableSettingsChanged] = React.useState(false);
  const { data: tableViews } = useSelector((state) => state.user.tablesViewState);
  const filteredGridData = useMemo(() =>
    filterAssets(data, portfolioIds, assetIds, assetType, assetsStatus, managers),
    [data, portfolioIds, assetIds, assetType, assetsStatus, managers]
  );
  const assetIdsForAdjustedGla = useMemo(
    () => filteredGridData.map((asset) => asset.id).filter(Boolean).join('|'),
    [filteredGridData]
  );
  const [adjustedGlaByAsset, setAdjustedGlaByAsset] = useState({});
  const gridData = useMemo(
    () =>
      filteredGridData.map((asset) => {
        const adjustedGla = adjustedGlaByAsset[asset.id];
        const monthlyRent = Number(asset.monthlyrent) || (Number(asset.totalrent) || 0) / 12;

        return {
          ...asset,
          avgrentpersqm: calculateMonthlyRentPerAdjustedGla(
            monthlyRent,
            adjustedGla,
            asset.avgrentpersqm || 0
          )
        };
      }),
    [adjustedGlaByAsset, filteredGridData]
  );

  // Default chart height
  const CHART_HEIGHT = 420; // Increased height
  const TEXT_COLOR = '#333333';

  // Base options for all charts
  const baseChartOptions = {
    chart: {
      foreColor: TEXT_COLOR,
      toolbar: { show: false }
    },
    xaxis: {
      labels: {
        style: {
          colors: [TEXT_COLOR],
          fontSize: '12px'
        }
      },
      title: {
        style: {
          color: TEXT_COLOR,
          fontSize: '14px'
        }
      },
      axisBorder: {
        show: true,
        color: '#ccc'
      },
      axisTicks: {
        show: true,
        color: '#ccc'
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: [TEXT_COLOR],
          fontSize: '12px'
        }
      },
      title: {
        style: {
          color: TEXT_COLOR,
          fontSize: '14px'
        }
      }
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 4,
      row: {
        colors: undefined,
        opacity: 0.5
      },
      column: {
        colors: undefined,
        opacity: 0.5
      }
    },
    legend: {
      labels: { colors: TEXT_COLOR },
      fontSize: '13px'
    },
    tooltip: {
      theme: 'light' // Use light theme for dark text on light background
    },
    dataLabels: {
      // Default data label settings (can be overridden)
      enabled: false, // Disable by default, enable per chart if needed
      style: {
        fontSize: '12px'
        // Avoid setting default color here, let individual charts handle contrast
      }
    }
    // Define a default color palette if desired (optional)
    // colors: ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0'],
  };

  // --- Define Border Style Object ---
  const chartBoxSx = {
    border: (theme) => `1px solid ${theme.palette.divider}`, // Use theme's divider color
    borderRadius: '4px', // Optional: Add border radius (match Paper's default or customize)
    p: 2, // Optional: Add padding inside the border (theme.spacing(2))
    // Keep layout styles from className if needed, or redefine here
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  };

  const handleChange = (_, newValue) => {
    setTab(newValue);
  };

  function getAssetDetailsPath(assetId) {
    return `/Asset/${assetId}/Details`;
  }

  function resetAssetStateSlices() {
    dispatch(actions.resetAssetDetails());
    dispatch(actions.resetValuations());
    dispatch(actions.resetActuals());
    dispatch(actions.resetOpexCostManager());
    dispatch(actions.resetAssetCostManager());
    dispatch(actions.resetMLA());
    dispatch(actions.resetGeoData());
  }

  function navigateToAssetDetails(assetId) {
    if (!assetId) {
      return;
    }

    resetAssetStateSlices();
    router.push(getAssetDetailsPath(assetId));
  }

  function renderAssetNameLink(params) {
    const assetId = params?.data?.id;
    if (!assetId) {
      return params?.value || '';
    }

    const handleAssetLinkClick = (event) => {
      const isPlainLeftClick =
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey;

      if (!isPlainLeftClick) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      navigateToAssetDetails(assetId);
    };

    return (
      <a
        href={getAssetDetailsPath(assetId)}
        onClick={handleAssetLinkClick}
        onContextMenu={(event) => event.stopPropagation()}
        style={{ color: 'inherit', textDecoration: 'none' }}
      >
        {params.value}
      </a>
    );
  }

  function openAssetInNewTab(assetId) {
    const assetPath = getAssetDetailsPath(assetId);
    window.open(assetPath, '_blank', 'noopener,noreferrer');
  }

  function handleAssetCellMouseDown(e) {
    const nativeEvent = e?.event;
    const colId = e?.column?.colId || e?.column?.getColId?.();
    if (!nativeEvent || !e?.data?.id || colId === 'rowSelection') {
      return;
    }

    if (nativeEvent.button === 1) {
      nativeEvent.preventDefault();
      nativeEvent.stopPropagation();
      openAssetInNewTab(e.data.id);
    }
  }

  function getAssetContextMenuItems(params) {
    const colId = params?.column?.colId || params?.column?.getColId?.();
    const assetId = params?.node?.data?.id || params?.data?.id;
    const defaultItems = params?.defaultItems || [];

    if (!assetId || colId === 'rowSelection') {
      return defaultItems;
    }

    return [
      {
        name: 'Open Asset in New Tab',
        action: () => openAssetInNewTab(assetId)
      },
      'separator',
      ...defaultItems
    ];
  }

  // Column definitions remain the same...
  const [colDefs, setColDefs] = useState([
    {
      headerName: '',
      field: 'rowSelection',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      maxWidth: 60
    },
    {
      headerName: 'Assets',
      field: 'name',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      cellRenderer: renderAssetNameLink
    },
    {
      headerName: 'City',
      field: 'city',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'Type',
      field: 'type',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'Portfolio',
      field: 'portfolioname',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'Tenants',
      field: 'tenantcount',
      filter: 'agSetColumnFilter',
      chartDataType: 'series',
      headerComponent: AGGridHeader
    },

    {
      headerName: 'Occupancy (%)',
      field: 'occupancy',
      filter: 'agNumberColumnFilter',
      chartDataType: 'series',
      headerComponent: AGGridHeader
    },
    {
      headerName: 'GLA (m2)',
      field: 'glatotal',
      filter: 'agNumberColumnFilter',
      cellRenderer: (params) => numberWithCommas(params.value),
      chartDataType: 'series',
      aggFunc: 'sum',
      headerComponent: AGGridHeader
    },
    {
      headerName: 'Current Year NOI (F)',
      field: 'noi',
      filter: 'agNumberColumnFilter',
      cellRenderer: (params) => numberWithCommas(params.value),
      chartDataType: 'series',
      aggFunc: 'sum',
      headerComponent: AGGridHeader,
      width: 240
    },
    {
      headerName: 'Net Yield (%)',
      field: 'yield',
      filter: 'agNumberColumnFilter',
      cellRenderer: (params) => formatZillions(params.value, '', 2),
      chartDataType: 'series',
      aggFunc: 'sum',
      headerComponent: AGGridHeader,
      hide: true
    },
    {
      headerName: 'Gross Yield (%)',
      field: 'grossyield',
      filter: 'agNumberColumnFilter',
      cellRenderer: (params) => formatZillions(params.value, '', 2),
      chartDataType: 'series',
      aggFunc: 'sum',
      headerComponent: AGGridHeader
    },
    {
      headerName: 'Average Rent / SQM',
      field: 'avgrentpersqm',
      filter: 'agNumberColumnFilter',
      cellRenderer: (params) => formatNumberToCurrency(params.value, 'EUR', '€0', 0),
      chartDataType: 'series',
      aggFunc: 'sum',
      headerComponent: AGGridHeader,
      width: 240
    },
    {
      headerName: `Current Rent / ${new Date().toLocaleString('default', { month: 'short' })}`,
      field: 'monthlyrent',
      filter: 'agNumberColumnFilter',
      cellRenderer: (params) => formatNumberToCurrency(params.value, 'EUR', '€0', 0),
      chartDataType: 'series',
      aggFunc: 'sum',
      headerComponent: AGGridHeader,
      width: 240
    },
    {
      headerName: 'Current Year Rent (F)',
      field: 'totalrent',
      filter: 'agNumberColumnFilter',
      cellRenderer: (params) => formatNumberToCurrency(params.value),
      chartDataType: 'series',
      aggFunc: 'sum',
      headerComponent: AGGridHeader,
      headerComponentParams: { wrapHeaderText: true },
      width: 270
    },
    {
      headerName: 'Comment',
      field: 'comment',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      headerComponent: AGGridHeader
    },
    {
      headerName: 'Manager',
      field: 'manager',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'L1 Fair Market Value',
      field: 'l1valuation',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      cellRenderer: currencyFormatter,
      headerComponent: AGGridHeader,
      width: 270
    },
    {
      headerName: 'L1 Liquidation value',
      field: 'l1liquidationvalue',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      cellRenderer: currencyFormatter,
      headerComponent: AGGridHeader,
      width: 270
    },
    {
      headerName: 'L1 Valuer',
      field: 'l1valuer',
      enableRowGroup: true,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'L1  Dcf Method Value',
      field: 'l1dcfmethodvalue',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'L1 Comparative Method Value',
      field: 'l1comparativemethodvalue',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      width: 270
    },
    {
      headerName: 'L1 Insurance Method Value',
      field: 'l1insurancemethodvalue',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      width: 270
    },
    {
      headerName: 'L1 ERV Rent (EUR/month)',
      field: 'l1currentrent',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      width: 270
    },
    {
      headerName: 'L1 ERV (EUR/sqm/month)',
      field: 'l1erv',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      width: 240
    },
    {
      headerName: 'L1 Discount Rate',
      field: 'l1discountrate',
      cellRenderer: percentageFormatter,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'L1 Exit Yield',
      field: 'l1exityield',
      cellRenderer: percentageFormatter,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'L2 Fair Market Value',
      field: 'l2valuation',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      cellRenderer: currencyFormatter,
      headerComponent: AGGridHeader,
      width: 270
    },
    {
      headerName: 'L2 Liquidation value',
      field: 'l2liquidationvalue',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      cellRenderer: currencyFormatter,
      headerComponent: AGGridHeader,
      width: 270
    },

    {
      headerName: 'L2 Valuer',
      field: 'l2valuer',
      enableRowGroup: true,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'L2  Dcf Method Value',
      field: 'l2dcfmethodvalue',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'L2 Comparative Method Value',
      field: 'l2comparativemethodvalue',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      width: 270
    },
    {
      headerName: 'L2 Insurance Method Value',
      field: 'l2insurancemethodvalue',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      width: 270
    },
    {
      headerName: 'L2 ERV Rent (EUR/month)',
      field: 'l2currentrent',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      width: 270
    },
    {
      headerName: 'L2 ERV (EUR/sqm/month)',
      field: 'l2erv',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      width: 240
    },
    {
      headerName: 'L2 Discount Rate',
      field: 'l2discountrate',
      cellRenderer: percentageFormatter,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'L2 Exit Yield',
      field: 'l2exityield',
      cellRenderer: percentageFormatter,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'L3 Fair Market Value',
      field: 'l3valuation',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      cellRenderer: currencyFormatter,
      headerComponent: AGGridHeader,
      width: 270
    },
    {
      headerName: 'L3 Liquidation value',
      field: 'l3liquidationvalue',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      cellRenderer: currencyFormatter,
      headerComponent: AGGridHeader,
      width: 270
    },

    {
      headerName: 'L3 Valuer',
      field: 'l3valuer',
      enableRowGroup: true,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'L3  Dcf Method Value',
      field: 'l3dcfmethodvalue',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'L3 Comparative Method Value',
      field: 'l3comparativemethodvalue',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      width: 270
    },
    {
      headerName: 'L3 Insurance Method Value',
      field: 'l3insurancemethodvalue',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      width: 270
    },
    {
      headerName: 'L3 ERV Rent (EUR/month)',
      field: 'l3currentrent',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      width: 270
    },
    {
      headerName: 'L3 ERV (EUR/sqm/month)',
      field: 'l3erv',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      width: 240
    },
    {
      headerName: 'L3 Discount Rate',
      field: 'l3discountrate',
      cellRenderer: percentageFormatter,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'L3 Exit Yield',
      field: 'l3exityield',
      cellRenderer: percentageFormatter,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'Asset Status',
      field: 'status',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      headerComponent: AGGridHeader
    },
    {
      headerName: 'Acquisition',
      field: 'acquisition',
      cellRenderer: currencyFormatter,
      enableRowGroup: true,
      headerComponent: AGGridHeader,
      width: 240
    },
    {
      headerName: 'SAP ID',
      field: 'clientAssetId',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true
    },
    {
      headerName: 'Profit center ID',
      field: 'profitCenter',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true
    },
    {
      headerName: 'Cost center ID',
      field: 'costCenter',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true
    }
  ]);

  function currencyFormatter(params) {
    return params.value === 0 ? '-' : formatNumberToCurrency(params.value);
  }

  function percentageFormatter(params) {
    if (params.value === 0) {
      return '-';
    }
    if (typeof params.value === 'number') {
      return `${params.value}%`;
    }
    return ''; // Or some other default for non-numeric types
  }

  useEffect(() => {
    if (tableViews.length === 0) {
      dispatch(getUserTableViews());
    }
  }, []);

  useEffect(() => {
    dispatch(actions.getAssets());
  }, [page]);

  useEffect(() => {
    let cancelled = false;
    const currentAssetIds = assetIdsForAdjustedGla ? assetIdsForAdjustedGla.split('|') : [];

    if (currentAssetIds.length === 0) {
      setAdjustedGlaByAsset({});
      return undefined;
    }

    getAdjustedGlaByAsset(currentAssetIds, { occupiedOnly: true })
      .then((result) => {
        if (!cancelled) {
          setAdjustedGlaByAsset(result);
        }
      })
      .catch((err) => {
        console.error('Failed to calculate adjusted GLA by asset', err);
      });

    return () => {
      cancelled = true;
    };
  }, [assetIdsForAdjustedGla]);

  useEffect(() => {
    dispatch(actions.getAssetReports(filter.assetIds, filter.portfolioIds, filter.assetTypes, filter.assetsStatus, filter.managers));
  }, [filter]);

  const onGridReady = async (params) => {
    setAPI(params.api);
  };

  if (error && loaded) {
    return (
      <div className={localClasses.root}>
        <Typography color="error" variant="h5">
          {error}
        </Typography>
      </div>
    );
  }

  const resetAssetDetailsState = (e) => {
    const nativeEvent = e?.event;
    if (
      e.column.colId !== 'name' ||
      !nativeEvent ||
      nativeEvent.target.closest('.MuiBadge-root') ||
      nativeEvent.target.closest('.ag-selection-checkbox') ||
      nativeEvent.defaultPrevented
    ) {
      return;
    }

    const isPlainLeftClick =
      nativeEvent.button === 0 &&
      !nativeEvent.metaKey &&
      !nativeEvent.ctrlKey &&
      !nativeEvent.shiftKey &&
      !nativeEvent.altKey;

    if (!isPlainLeftClick) {
      return;
    }

    navigateToAssetDetails(e.data.id);
  };

  const asseTabletViews = useMemo(
    () => tableViews.filter((it) => it.tableviewtypename === ASSETS_TABLE_VIEW_TYPE_NAME),
    [tableViews]
  );

  return (
    <Container>
      <Suspense fallback={<Spinner mini />}>
        <Grow in>
          <Box
            className={clsx(classes.containerLarge)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1
            }}
          >
            <Box className={classes.header}>
              <Typography className={classes.title}>{`Assets (${gridData?.length ?? 0})`}</Typography>
            </Box>now
            <Tabs
              value={tab}
              handleChange={handleChange}
              tabNames={['Table', 'Charts', 'Macro Assumptions']}
            />
            {/* --- CHARTS TAB --- */}
            <TabPanel value={tab} index={1}>
              <Box
                sx={{
                  flexGrow: 1,
                  py: 2,
                  minHeight: '75vh'
                }}
              >
                <CoreFilters filtersUpdated={setFilter} targetAssetIdsUpdated={() => {}} />
                <Paper
                  sx={{
                    p: 4,
                    width: '100%',
                    height: '100%'
                  }}
                >
                  {reportsLoaded ? (
                    <Grid container spacing={4} alignItems="stretch">
                      {/* --- Chart 1: Asset Type by Count (Bar) --- */}
                      <Grid item xs={12} md={6}>
                        {/* Apply sx prop to the Box */}
                        <Box className={localClasses.chartContainer} sx={chartBoxSx}>
                          <Typography className={localClasses.chartTitle}>
                            Asset Type by Count
                          </Typography>
                          <div style={{ width: '100%' }}>
                            <ApexChart
                              type="bar"
                              width="100%"
                              height={CHART_HEIGHT}
                              options={{
                                ...baseChartOptions, // Apply base options
                                xaxis: {
                                  ...baseChartOptions.xaxis,
                                  type: 'category' // X-axis is categorical
                                },
                                yaxis: {
                                  ...baseChartOptions.yaxis,
                                  labels: {
                                    ...baseChartOptions.yaxis.labels,
                                    // Format Y-axis to integer (0 decimals)
                                    formatter: (value) => {
                                      const numValue = parseFloat(value);
                                      return isNaN(numValue) ? '' : numValue.toFixed(0);
                                    }
                                  }
                                },
                                tooltip: {
                                  ...baseChartOptions.tooltip,
                                  y: {
                                    // Format tooltip to integer (0 decimals)
                                    formatter: (value) => {
                                      const numValue = parseFloat(value);
                                      return isNaN(numValue) ? '' : numValue.toFixed(0);
                                    }
                                  }
                                },
                                legend: {
                                  position: 'right'
                                }
                              }}
                              series={[
                                {
                                  name: 'Count', // Series name for legend/tooltip
                                  data: (asset_reports?.assetCountByType?.dataMap ?? []).map(
                                    (item) => ({
                                      x: item[0],
                                      y: item[1]
                                    })
                                  )
                                }
                              ]}
                            />
                          </div>
                        </Box>
                      </Grid>

                      {/* --- Chart 2: Average Gross Yield by Asset Type (Bar) --- */}
                      <Grid item xs={12} md={6}>
                        {/* Apply sx prop to the Box */}
                        <Box className={localClasses.chartContainer} sx={chartBoxSx}>
                          <Typography className={localClasses.chartTitle}>
                            Average Gross Yield by Asset Type
                          </Typography>
                          <div style={{ width: '100%' }}>
                            <ApexChart
                              type="bar"
                              width="100%"
                              height={CHART_HEIGHT}
                              options={{
                                ...baseChartOptions, // Apply base options
                                xaxis: {
                                  ...baseChartOptions.xaxis,
                                  type: 'category'
                                },
                                yaxis: {
                                  ...baseChartOptions.yaxis,
                                  labels: {
                                    ...baseChartOptions.yaxis.labels,
                                    // Format Y-axis as percentage with 2 decimals
                                    formatter: (value) => {
                                      const numValue = parseFloat(value);
                                      return isNaN(numValue) ? '' : `${numValue.toFixed(2)}%`;
                                    }
                                  }
                                },
                                tooltip: {
                                  ...baseChartOptions.tooltip,
                                  y: {
                                    // Format tooltip as percentage with 2 decimals
                                    formatter: (value) => {
                                      const numValue = parseFloat(value);
                                      return isNaN(numValue) ? '' : `${numValue.toFixed(2)}%`;
                                    }
                                  }
                                },
                                legend: {
                                  position: 'right'
                                }
                              }}
                              series={[
                                {
                                  name: 'Avg. Gross Yield', // Series name
                                  data:
                                    (asset_reports?.averageGrossYieldByType?.dataMap ?? []).map(
                                      (item) => ({
                                        x: item[0],
                                        y: Math.round(item[1])
                                      })
                                    ) ?? []
                                }
                              ]}
                            />
                          </div>
                        </Box>
                      </Grid>

                      {/* --- Chart 3: GLA by Energy Score (Bar) --- */}
                      <Grid item xs={12} md={6}>
                        {/* Apply sx prop to the Box */}
                        <Box className={localClasses.chartContainer} sx={chartBoxSx}>
                          <Typography className={localClasses.chartTitle}>
                            GLA by Energy Score
                          </Typography>
                          <div style={{ width: '100%' }}>
                            <ApexChart
                              type="bar"
                              width="100%"
                              height={CHART_HEIGHT}
                              options={{
                                ...baseChartOptions,
                                chart: {
                                  ...baseChartOptions.chart,
                                  foreColor: TEXT_COLOR
                                }, // Ensure dark text
                                colors: [
                                  '#008FFB',
                                  '#00E396',
                                  '#FEB019',
                                  '#FF4560',
                                  '#775DD0',
                                  '#546E7A',
                                  '#26a69a'
                                ],
                                plotOptions: {
                                  bar: {
                                    distributed: true,
                                    dataLabels: { position: 'top' }
                                  }
                                },
                                dataLabels: {
                                  enabled: true,
                                  formatter: (value) => {
                                    const numValue = parseFloat(value);
                                    if (isNaN(numValue)) return '';
                                    // Apply formatZillions ONLY if it exists and works, otherwise just show the number
                                    return typeof formatZillions === 'function'
                                      ? formatZillions(numValue)
                                      : numValue.toFixed(0);
                                  },
                                  offsetY: -20,
                                  style: {
                                    fontSize: '12px',
                                    colors: [TEXT_COLOR]
                                  }
                                },
                                xaxis: {
                                  ...baseChartOptions.xaxis,
                                  type: 'category',
                                  title: {
                                    ...baseChartOptions.xaxis.title,
                                    text: 'Energy Score'
                                  }
                                },
                                yaxis: {
                                  ...baseChartOptions.yaxis,
                                  title: {
                                    ...baseChartOptions.yaxis.title,
                                    text: 'GLA (sqm)'
                                  },
                                  labels: {
                                    ...baseChartOptions.yaxis.labels,
                                    formatter: (value) => {
                                      const numValue = parseFloat(value);
                                      if (isNaN(numValue)) return '';
                                      return typeof formatZillions === 'function'
                                        ? formatZillions(numValue)
                                        : 0;
                                    }
                                  }
                                },
                                tooltip: {
                                  ...baseChartOptions.tooltip,
                                  y: {
                                    formatter: (value) => {
                                      const numValue = parseFloat(value);
                                      if (isNaN(numValue)) return '';
                                      return typeof formatZillions === 'function'
                                        ? formatZillions(numValue)
                                        : 0;
                                    }
                                  }
                                },
                                legend: {
                                  position: 'right'
                                }
                              }}
                              series={[
                                {
                                  name: 'GLA',
                                  data:
                                    (asset_reports?.glaByEnergyScore?.dataMap ?? []).map(
                                      (item) => ({
                                        x: item[0],
                                        y: item[1]
                                      })
                                    ) ?? []
                                }
                              ]}
                            />
                          </div>
                        </Box>
                      </Grid>

                      {/* --- Chart 4: GLA (sqm) by Asset Type (Pie) --- */}
                      <Grid item xs={12} md={6}>
                        {/* Apply sx prop to the Box */}
                        <Box className={localClasses.chartContainer} sx={chartBoxSx}>
                          <Typography className={localClasses.chartTitle}>
                            GLA (sqm) by Asset Type
                          </Typography>
                          <div style={{ width: '100%' }}>
                            <ApexChart
                              type="pie"
                              width="100%"
                              height={CHART_HEIGHT}
                              options={{
                                ...baseChartOptions, // Apply base options
                                labels:
                                  (asset_reports?.glaByAssetType?.dataMap ?? []).map(
                                    (item) => item[0]
                                  ) ?? [],
                                legend: {
                                  ...baseChartOptions.legend,
                                  position: 'bottom'
                                },
                                tooltip: {
                                  ...baseChartOptions.tooltip,
                                  y: {
                                    formatter: (value) => {
                                      const numValue = parseFloat(value);
                                      if (isNaN(numValue)) return '';
                                      return typeof formatZillions === 'function'
                                        ? formatZillions(numValue.toFixed(0))
                                        : numValue.toFixed(0);
                                    },
                                    title: { formatter: (seriesName) => seriesName }
                                  }
                                },
                                dataLabels: {
                                  enabled: true,
                                  formatter: (val, opts) => {
                                    const numVal = parseFloat(val);
                                    return isNaN(numVal) ? '' : `${numVal.toFixed(1)}%`;
                                  },
                                  style: {
                                    ...baseChartOptions.dataLabels.style,
                                    colors: undefined // Let ApexCharts handle contrast
                                  },
                                  dropShadow: {
                                    enabled: true,
                                    top: 1,
                                    left: 1,
                                    blur: 1,
                                    color: '#000',
                                    opacity: 0.45
                                  }
                                },
                                responsive: [
                                  {
                                    breakpoint: 600,
                                    options: {
                                      chart: {
                                        width: '100%',
                                        height: CHART_HEIGHT * 0.8
                                      },
                                      legend: { position: 'bottom' }
                                    }
                                  }
                                ],
                                legend: {
                                  position: 'right'
                                }
                              }}
                              series={
                                (asset_reports?.glaByAssetType?.dataMap ?? []).map(
                                  (item) => item[1]
                                ) ?? []
                              }
                            />
                          </div>
                        </Box>
                      </Grid>

                      {/* --- Chart 5: Total Valuations by Asset Type (Pie) --- */}
                      <Grid item xs={12} md={6}>
                        {/* Apply sx prop to the Box */}
                        <Box className={localClasses.chartContainer} sx={chartBoxSx}>
                          <Typography className={localClasses.chartTitle}>
                            Total Valuations by Asset Type
                          </Typography>
                          <div style={{ width: '100%' }}>
                            <ApexChart
                              type="pie"
                              width="100%"
                              height={CHART_HEIGHT}
                              options={{
                                ...baseChartOptions, // Apply base options
                                labels:
                                  (asset_reports?.valuationByAssetType?.dataMap ?? []).map(
                                    (item) => item[0]
                                  ) ?? [],
                                legend: {
                                  ...baseChartOptions.legend,
                                  position: 'bottom'
                                },
                                tooltip: {
                                  ...baseChartOptions.tooltip,
                                  y: {
                                    formatter: (value) => {
                                      const numValue = parseFloat(value);
                                      if (isNaN(numValue)) return '';
                                      // Apply formatNumberToCurrency ONLY if it exists and works
                                      return typeof formatNumberToCurrency === 'function'
                                        ? formatNumberToCurrency(numValue.toFixed(2))
                                        : numValue.toFixed(2);
                                    },
                                    title: { formatter: (seriesName) => seriesName }
                                  }
                                },
                                dataLabels: {
                                  enabled: true,
                                  formatter: (val, opts) => {
                                    const numVal = parseFloat(val);
                                    return isNaN(numVal) ? '' : `${numVal.toFixed(1)}%`;
                                  },
                                  style: {
                                    ...baseChartOptions.dataLabels.style,
                                    colors: undefined // Let ApexCharts handle contrast
                                  },
                                  dropShadow: {
                                    enabled: true,
                                    top: 1,
                                    left: 1,
                                    blur: 1,
                                    color: '#000',
                                    opacity: 0.45
                                  }
                                },
                                responsive: [
                                  {
                                    breakpoint: 600,
                                    options: {
                                      chart: {
                                        width: '100%',
                                        height: CHART_HEIGHT * 0.8
                                      },
                                      legend: { position: 'bottom' }
                                    }
                                  }
                                ],
                                legend: {
                                  position: 'right'
                                }
                              }}
                              series={
                                (asset_reports?.valuationByAssetType?.dataMap ?? []).map(
                                  (item) => item[1]
                                ) ?? []
                              }
                            />
                          </div>
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%'
                      }}
                    >
                      <Spinner />
                    </Box>
                  )}
                </Paper>
              </Box>
            </TabPanel>

            {/* --- TABLE TAB --- */}
            <TabPanel value={tab} index={0}>
              <Box className={clsx(classes.table)} sx={{ flexGrow: 1 }}>
                <AGGridTable
                  onGridReady={onGridReady}
                  data={gridData}
                  colDefs={colDefs}
                  classes={classes}
                  onCellClicked={resetAssetDetailsState}
                  onCellMouseDown={handleAssetCellMouseDown}
                  getContextMenuItems={getAssetContextMenuItems}
                  deleteHandler={(selectedRows, api) => {
                    api.applyTransaction({ remove: selectedRows });
                    for (let i = 0; i < selectedRows.length; i++) {
                      dispatch(actions.deleteAsset(selectedRows[i].id))
                        .then(() => {})
                        .catch((err) => toast.error(err));
                    }
                  }}
                  buttons={[
                    EditAssetsPermissions && (
                      <Button
                        key="new-asset-btn"
                        className={classes.ctaButton}
                        style={{ marginRight: '10px' }}
                        startIcon={<Icon style={{ color: '#5381EF' }}>add</Icon>}
                        onClick={() =>
                          dispatch(
                            openModal({
                              modalType: 'AssetModal',
                              modalProps: { asset: { portfolioid: portfolioIds[0] } }
                            })
                          )
                        }
                      >
                        NEW
                      </Button>
                    )
                  ]}
                  rowGroupPanelShow={'always'}
                  groupDisplayType={'multipleColumns'}
                  tableSettingsChanged={tableSettingsChanged}
                  onTableSettingsChanged={setTableSettingsChanged}
                  tableViewTypeName={ASSETS_TABLE_VIEW_TYPE_NAME}
                  tableViews={asseTabletViews}
                ></AGGridTable>
              </Box>
            </TabPanel>
            <TabPanel value={tab} index={2}>
              <Box
                sx={{
                  flexGrow: 1,
                  py: 2
                }}
                style={{ background: '#fff' }}
              >
                <GCPI />
              </Box>
            </TabPanel>
          </Box>
        </Grow>
      </Suspense>
    </Container>
  );
};

export default Assets;

const ASSETS_TABLE_VIEW_TYPE_NAME = 'Assets';

function filterAssets(assets, portfolioIds, assetIds, assetType, assetsStatus, managers) {
  return assets.filter(
    (item) =>
      (portfolioIds.length === 0 || portfolioIds.includes(item.portfolioid)) &&
      (assetIds.length === 0 || assetIds.includes(item.id)) &&
      (assetType.length === 0 || assetType.includes(item.type)) &&
      (assetsStatus.length === 0 || assetsStatus.includes(item.status)) &&
      (managers.length === 0 || managers.includes(item.manager))
  );
}
